// Un progetto, su una pagina sua (tela, artboard 25): `/progetti/nuovo` per l'inserimento,
// `/progetti/<id>` per la modifica. Le scelte stanno in schede (generale, spazio di lavoro, git di
// riferimento, commit, push e pull request), la barra delle azioni resta in fondo. Ogni voce è
// una scelta dell'utente; un progetto nuovo parte dalle proposte del server, modificabili.
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import type { GitRegistry, GitRepo, Project, ProjectInput } from '../../lib/api';
import { useBreakpoint } from '../../lib/useMedia';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextField } from '../../components/Field';
import { Segmented } from '../../components/Segmented';
import { Select } from '../../components/Select';
import { SkeletonRow } from '../../components/Skeleton';
import { Switch } from '../../components/Switch';
import { useStore } from '../../store/StoreProvider';
import { ProjectOverview } from './ProjectOverview';
import { ProjectPlanTab } from './ProjectPlanTab';
import { RepoPicker } from './RepoPicker';

type Defaults = Omit<ProjectInput, 'name' | 'description'>;

function detail(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.detail ? e.detail : fallback;
}

function Card({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-xl)',
        background: 'var(--panel)',
        padding: '16px 18px',
        display: 'grid',
        gap: 12,
        minWidth: 0,
      }}
    >
      <div>
        <h3 className="t-h3" style={{ margin: 0 }}>
          {title}
        </h3>
        <div className="t-caption dim">{hint}</div>
      </div>
      {children}
    </section>
  );
}

const pair = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 12,
} as const;

// la pagina: legge progetto, proposte e registri, poi mostra il modulo
export function ProjectPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast, openModal } = useStore();
  const [data, setData] = useState<{
    project: Project | null;
    defaults: Defaults;
    registries: GitRegistry[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'plan' | 'settings'>('overview');

  useEffect(() => {
    Promise.all([api.projects(), api.gitRegistries()]).then(
      ([p, g]) => {
        const project = id ? (p.projects.find((x) => x.id === id) ?? null) : null;
        if (id && !project) setError('Progetto inesistente: forse è stato tolto.');
        else setData({ project, defaults: p.defaults, registries: g.registries });
      },
      (e: unknown) => setError(detail(e, 'Non riesco a leggere il progetto.')),
    );
  }, [id]);

  const remove = (p: Project) =>
    openModal({
      kind: 'confirm',
      title: `Togliere «${p.name}»?`,
      okLabel: 'Togli',
      danger: true,
      body: 'Il progetto esce dall’elenco: i file sul disco e il repository restano.',
      payload: {
        run: async () => {
          try {
            await api.removeProject(p.id);
            toast({ kind: 'success', title: `${p.name}: tolto` });
            nav('/progetti');
          } catch (e) {
            toast({ kind: 'danger', title: 'Non si toglie', body: detail(e, 'Riprova.') });
          }
        },
      },
    });

  return (
    <div className="page">
      <div className="t-caption dim">
        <Link to="/progetti" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          Progetti
        </Link>{' '}
        › {id ? (data?.project?.name ?? '…') : 'Nuovo'}
      </div>
      <h1 className="t-h1" style={{ margin: '4px 0 16px' }}>
        {id ? (data?.project?.name ?? 'Progetto') : 'Nuovo progetto'}
      </h1>
      {error && <Banner kind="danger">{error}</Banner>}
      {!data && !error && <SkeletonRow />}
      {data?.project && (
        <div style={{ marginBottom: 16 }}>
          <Segmented
            label="Schede del progetto"
            value={tab}
            items={[
              { value: 'overview', label: 'Panoramica' },
              { value: 'plan', label: 'Piano' },
              { value: 'settings', label: 'Impostazioni' },
            ]}
            onChange={setTab}
          />
        </div>
      )}
      {data?.project && tab === 'overview' && (
        <ProjectOverview
          project={data.project}
          registries={data.registries}
          onSettings={() => setTab('plan')}
        />
      )}
      {data?.project && tab === 'plan' && (
        <ProjectPlanTab
          project={data.project}
          onSaved={(project) => setData({ ...data, project })}
        />
      )}
      {data && (!data.project || tab === 'settings') && (
        <ProjectEditor
          key={data.project?.id ?? 'new'}
          project={data.project}
          defaults={data.defaults}
          registries={data.registries}
          onClose={() => nav('/progetti')}
          onRemove={remove}
          onSaved={(p) => {
            toast({ kind: 'success', title: `${p.name}: salvato` });
            nav('/progetti');
          }}
        />
      )}
    </div>
  );
}

export function ProjectEditor({
  project,
  defaults,
  registries,
  onSaved,
  onRemove,
  onClose,
}: {
  project: Project | null;
  defaults: Defaults;
  registries: GitRegistry[];
  onSaved: (p: Project) => void;
  onRemove: (p: Project) => void;
  onClose: () => void;
}) {
  const bp = useBreakpoint();
  const [form, setForm] = useState<ProjectInput>(
    project ?? { ...defaults, name: '', description: '' },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repos, setRepos] = useState<GitRepo[] | null>(null);
  const set = (patch: Partial<ProjectInput>) => setForm((f) => ({ ...f, ...patch }));
  const registry = registries.find((r) => r.id === form.git?.registry) ?? null;
  const hasApi = registry !== null && registry.kind !== 'git';

  // i repository del registro scelto, per sceglierli dall'elenco
  useEffect(() => {
    setRepos(null);
    if (!registry || registry.kind === 'git') return;
    api.gitRepos(registry.id).then(
      (r) => setRepos(r.repos),
      () => setRepos([]),
    );
  }, [registry]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(project ? await api.saveProject(project.id, form) : await api.addProject(form));
    } catch (e) {
      setError(detail(e, 'Non riesco a salvare.'));
      setBusy(false);
    }
  };

  const general = (
    <Card title="Generale" hint="Come si chiama e a cosa serve.">
      <Field label="Nome">
        <TextField value={form.name} onChange={(v) => set({ name: v })} autoFocus={!project} />
      </Field>
      <Field label="Descrizione">
        <TextField value={form.description} onChange={(v) => set({ description: v })} />
      </Field>
    </Card>
  );

  const space = (
    <Card
      title="Spazio di lavoro"
      hint="Dove stanno i file. Le sessioni del progetto li condividono."
    >
      <Field label="Tipo">
        <Segmented<string>
          label="Tipo di spazio"
          value={form.workspace.kind}
          items={[
            { value: 'local', label: 'Locale' },
            { value: 'smb', label: 'Volume SMB · in arrivo', disabled: true },
          ]}
          onChange={() => undefined}
        />
      </Field>
      <Field
        label="Cartella sul disco di PopTown"
        hint="Dentro works/projects/. Vuota: il nome del progetto."
      >
        <TextField
          mono
          value={form.workspace.folder}
          onChange={(v) => set({ workspace: { kind: 'local', folder: v } })}
          placeholder="poptown"
        />
      </Field>
      <Field
        label="Allegati: dimensione massima per file (MB)"
        hint="Gli allegati delle richieste vanno in docs/, nel repository se c'è il git."
      >
        <TextField
          value={String(form.docsMaxMb)}
          onChange={(v) => set({ docsMaxMb: Number(v.replace(/\D/g, '')) || 0 })}
          suffix="MB"
        />
      </Field>
    </Card>
  );

  const commits = (
    <Card title="Commit" hint="Come firmano i Pops.">
      <div style={pair}>
        <Field label="Autore" hint="{agente} diventa il nome del pop, es. Sviluppatore">
          <TextField value={form.commitAuthor} onChange={(v) => set({ commitAuthor: v })} />
        </Field>
        <Field label="Email">
          <TextField mono value={form.commitEmail} onChange={(v) => set({ commitEmail: v })} />
        </Field>
      </div>
      <Field
        label="Messaggio"
        hint="{titolo} diventa il titolo del lavoro; anche {agente} e {sessione}"
      >
        <TextField mono value={form.commitMessage} onChange={(v) => set({ commitMessage: v })} />
      </Field>
    </Card>
  );

  const git = (
    <Card title="Git di riferimento" hint="Facoltativo: senza git i commit restano solo in locale.">
      <div style={pair}>
        <Field label="Registro">
          <Select
            label="Registro"
            value={form.git?.registry ?? ''}
            onChange={(v) => set({ git: v ? { registry: v, repo: form.git?.repo ?? '' } : null })}
            options={[
              { value: '', label: 'Nessun git' },
              ...registries.map((r) => ({ value: r.id, label: r.name })),
            ]}
            width="100%"
          />
        </Field>
        {form.git && registry && (
          <Field
            label="Repository"
            hint={hasApi ? 'Dall’elenco del server, o «Crea nuovo».' : 'proprietario/nome'}
          >
            <RepoPicker
              registry={registry}
              repos={repos}
              value={form.git.repo}
              onChange={(repo, branch) => {
                set({ git: { registry: registry.id, repo } });
                if (branch && !project) set({ baseBranch: branch });
              }}
            />
          </Field>
        )}
      </div>
      {registries.length === 0 && (
        <div className="t-caption dim">
          Nessun git nell’archivio: si aggiunge nella pagina{' '}
          <Link to="/git" style={{ color: 'var(--accent)' }}>
            Git
          </Link>
          .
        </div>
      )}
      <div style={pair}>
        <Field label="Branch di partenza">
          <TextField mono value={form.baseBranch} onChange={(v) => set({ baseBranch: v })} />
        </Field>
        <Field
          label="Branch di lavoro"
          hint="{sessione} diventa l'id della sessione (es. poptown/ses_8f3a2c): un branch per sessione. Senza, lo stesso per tutte."
        >
          <TextField mono value={form.workBranch} onChange={(v) => set({ workBranch: v })} />
        </Field>
      </div>
    </Card>
  );

  const push = (
    <Card title="Push e pull request" hint="Quando il lavoro arriva al server.">
      <Field label="Push" hint={form.git ? undefined : 'Serve un git di riferimento.'}>
        <Segmented
          label="Push"
          value={form.push}
          onChange={(v) => set({ push: v })}
          items={[
            { value: 'task', label: 'Dopo ogni lavoro' },
            { value: 'mission', label: 'A fine missione' },
            { value: 'manual', label: 'Solo a mano' },
          ]}
        />
      </Field>
      <Field
        label="Pull request"
        hint="A fine missione, verso il branch di partenza. Solo Gitea e GitHub."
      >
        <Segmented
          label="Pull request"
          value={form.pullRequest}
          onChange={(v) => set({ pullRequest: v })}
          items={[
            { value: 'none', label: 'Nessuna' },
            { value: 'open', label: 'Aperta da PopTown' },
            { value: 'merge', label: 'Aperta e unita' },
          ]}
        />
      </Field>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          padding: '10px 12px',
        }}
      >
        <Switch
          checked={form.default}
          onChange={(v) => set({ default: v })}
          label="Progetto predefinito"
          hint="Già scelto in «Nuova richiesta»."
        />
      </div>
    </Card>
  );

  const column = { display: 'grid', gap: 14, alignContent: 'start', minWidth: 0 } as const;
  return (
    <>
      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner kind="danger">{error}</Banner>
        </div>
      )}
      {bp.md ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={column}>
            {general}
            {space}
            {commits}
          </div>
          <div style={column}>
            {git}
            {push}
          </div>
        </div>
      ) : (
        <div style={column}>
          {general}
          {space}
          {git}
          {commits}
          {push}
        </div>
      )}
      <div
        style={{
          // sul telefono il menu in basso è già fisso: la barra resta in fondo alla pagina
          position: bp.md ? 'sticky' : 'static',
          bottom: 0,
          marginTop: 16,
          padding: '12px 0',
          background: 'var(--bg)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        {project ? (
          <Button icon="trash" onClick={() => onRemove(project)}>
            Togli
          </Button>
        ) : (
          <span />
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!form.name.trim() || (form.git !== null && !form.git.repo.trim())}
            onClick={() => void save()}
          >
            {project ? 'Salva' : 'Crea progetto'}
          </Button>
        </div>
      </div>
    </>
  );
}
